import * as React from 'react';
import type { DrawerProps } from '@fluentui/react-components';
import { makeStyles, OverlayDrawer } from '@fluentui/react-components';
import { useMediaQuery } from '@react-hook/media-query';
import { type ClientEntryPointEnum, useUserQuery } from '@foundershub/api-client';
import { screenSizes } from '../modules/breakpoints';
import { setCopilotTextarea, useDispatch } from '../store';

interface DrawerContext {
  showDrawer: boolean;
  setShowDrawer: (show: boolean | ((shown: boolean) => boolean)) => void;
  children: React.ReactNode | null;
  setChildren: (Component: React.ReactNode | null, size?: DrawerProps['size']) => void;
  drawerSize?: DrawerProps['size'];
}

interface CopilotContext {
  openCopilot: (openedFrom: ClientEntryPointEnum) => void;
  closeCopilot: () => void;
  isSidecarOpen: boolean;
  // TODO: consider removing ability to manually set sidecar open
  // once we can control this using a field from the backend
  setSidecarOpen: (open: boolean) => void;
  isFREOpen: boolean;
  setFREOpen: (open: boolean) => void;
  isConfirmationOpen: boolean;
  setConfirmationOpen: (open: boolean) => void;
  openedFrom?: ClientEntryPointEnum;
  categoryId: number;
  setCategoryId: (categoryId: number) => void;
}

export const DrawerContext = React.createContext<DrawerContext>({
  showDrawer: false,
  setShowDrawer: () => {},
  setChildren: () => {},
  children: null,
  drawerSize: undefined,
});

export const CopilotContext = React.createContext<CopilotContext>({
  openCopilot: () => {},
  closeCopilot: () => {},
  isSidecarOpen: false,
  setSidecarOpen: () => {},
  isFREOpen: false,
  setFREOpen: () => {},
  isConfirmationOpen: false,
  setConfirmationOpen: () => {},
  setCategoryId: () => {},
  categoryId: -1,
});

export function DrawerProvider({ children }: { children: React.ReactNode }) {
  const { data: user, isError: isUserLoggedOut } = useUserQuery();
  const dispatch = useDispatch();
  const [showDrawer, setShowDrawer] = React.useState(false);
  const [drawerChildren, setChildren] = React.useState<React.ReactNode>(null);
  const [drawerSize, setSize] = React.useState<DrawerProps['size']>(undefined);
  const [sidecarOpen, setSidecarOpen] = React.useState(false);
  const [freOpen, setFREOpen] = React.useState(false);
  const [confirmationOpen, setConfirmationOpen] = React.useState(false);
  const [openedFrom, setOpenedFrom] = React.useState<ClientEntryPointEnum>();
  const [categoryId, setCategoryId] = React.useState<number>(-1);
  function setCopilotOpenWithDrawer(open: boolean) {
    return isUserLoggedOut
      ? () => {}
      : open
        ? (openedFrom?: ClientEntryPointEnum) => {
            const openedFromForm = openedFrom == 'ata-form' || openedFrom == 'support-form';
            if (!user!.signed_copilot_agreement_on) {
              setFREOpen(openedFromForm || !sidecarOpen); // leave the sidecar open if it's already open
            } else if (openedFromForm) {
              setConfirmationOpen(true);
            } else {
              setSidecarOpenWithDrawer(true);
            }
            setOpenedFrom(openedFrom);
          }
        : () => {
            setFREOpen(false);
            setConfirmationOpen(false);
            setSidecarOpenWithDrawer(false);
            setCategoryId(-1);
          };
  }

  function setSidecarOpenWithDrawer(open: boolean) {
    if (!open) {
      dispatch(setCopilotTextarea(''));
    }
    setSidecarOpen(open);
    setShowDrawer(false);
  }

  return (
    <DrawerContext.Provider
      value={{
        showDrawer,
        setShowDrawer,
        setChildren: (children: React.ReactNode, drawerSize?: DrawerProps['size']) => {
          setChildren(children);
          setSize(drawerSize || 'medium');
        },
        drawerSize,
        children: drawerChildren,
      }}
    >
      <CopilotContext.Provider
        value={{
          openCopilot: setCopilotOpenWithDrawer(true),
          closeCopilot: setCopilotOpenWithDrawer(false),
          isSidecarOpen: sidecarOpen,
          setSidecarOpen: setSidecarOpenWithDrawer,
          isFREOpen: freOpen,
          setFREOpen,
          isConfirmationOpen: confirmationOpen,
          setConfirmationOpen,
          openedFrom,
          categoryId,
          setCategoryId,
        }}
      >
        {children}
      </CopilotContext.Provider>
    </DrawerContext.Provider>
  );
}

export function FoundersHubUserOverlayDrawer() {
  const classes = useStyles();
  const isSmallScreen = useMediaQuery(`screen and (max-width: ${screenSizes.xs})`);
  const [topOffset, setTopOffset] = React.useState(0);

  const handleScroll = () => {
    if (document.getElementsByTagName('header').length === 0) {
      setTopOffset(0);
      return;
    }

    const headerHeight = document.getElementsByTagName('header')[0].clientHeight ?? 0;
    const headerTopY = document.getElementsByTagName('header')[0].getBoundingClientRect().top ?? 0;
    const offset = headerTopY > -1 * headerHeight ? headerTopY + headerHeight : 0;
    setTopOffset(offset);
  };

  React.useEffect(() => {
    window.addEventListener('resize', handleScroll, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('resize', handleScroll);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const responsiveWidth = isSmallScreen ? '100%' : '420px';
  const { children, showDrawer, drawerSize } = React.useContext(DrawerContext);

  return (
    <OverlayDrawer
      position="end"
      open={showDrawer}
      style={{
        ...(drawerSize && !isSmallScreen ? {} : { width: 'auto' }),
        ...(drawerSize && !isSmallScreen ? {} : { minWidth: responsiveWidth }),
        top: `${topOffset}px`,
        height: `calc(100vh - ${topOffset}px)`,
      }}
      className={classes.drawer}
      backdrop={{ className: classes.backdrop, style: { top: `${topOffset}px` } }}
      size={drawerSize}
    >
      {children}
    </OverlayDrawer>
  );
}

const useStyles = makeStyles({
  drawer: {
    zIndex: 1e14,
  },
  backdrop: {
    position: 'fixed',
    zIndex: 1e13,
  },
});
